// react
import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { PillsInput, Pill, Combobox, useCombobox, LoadingOverlay, Image, Loader, Text } from "@mantine/core";
import { Search } from "lucide-react";
import { useDispatch } from 'react-redux';
import { setCurrentGame, setSearchQuery } from '@/store/features/gameSlice';
import { useRouter } from 'next/router';

// scss
import styles from "./search.module.scss";

// utils
import debounce, { ensureAbsoluteImageUrl } from "@/utils/methods";
import { gameClicked } from "@/utils/event";
import grpcApi from '@/lib/api/grpcApi';

export default function SearchInput() {
    const [gameList, setGameList] = useState([]);
    const [fetchLoading, setFetchLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [searchInput, setSearchInput] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [totalResults, setTotalResults] = useState(0);
    const abortControllerRef = useRef(null);
    const requestIdRef = useRef(0); // 添加请求序列号来避免竞争条件
    const lastSearchQueryRef = useRef(""); // 记录上次搜索的查询
    const dropdownRef = useRef(null);
    const dispatch = useDispatch();
    const searchIcon = <Search size={16} onClick={() => dispatch(setSearchQuery(searchInput))} />;
    const router = useRouter();
    const combobox = useCombobox({
        onDropdownClose: () => combobox.resetSelectedOption(),
    });

    const filterGameList = useMemo(() => {
        const searchInputTrim = searchInput.trim();

        return !searchInputTrim ? [] : gameList?.reduce((arr, item) => {
            if (item.name && item.name.toLowerCase().includes(searchInputTrim.toLowerCase())) {
                arr.push({
                    value: item.path,
                    label: item.name || item.path,
                    game: item,
                });
            }
            return arr;
        }, []);
    }, [searchInput, gameList]);

    // 加载更多游戏
    const fetchMoreGames = useCallback(async () => {
        const query = searchInput.trim();
        if (!query || loadingMore || !hasMore) return;

        const currentRequestId = ++requestIdRef.current;

        try {
            // 取消之前的请求
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
            
            // 创建新的AbortController
            abortControllerRef.current = new AbortController();

            setLoadingMore(true);
            
            // 使用封装的grpcApi搜索方法
            const result = await grpcApi.game.searchGames({
                query: query,
                page: currentPage + 1,
                page_size: 10
            }, abortControllerRef.current);

            // 检查请求是否已经过期
            if (currentRequestId !== requestIdRef.current) {
                console.log('Load more request is outdated, ignoring result');
                return;
            }

            // 安全处理搜索结果
            const rawResults = result.data?.results;
            if (!Array.isArray(rawResults)) {
                return;
            }

            const newResults = rawResults.map(item => ({
                ...item,
                path: item.slug,
                cover_image: ensureAbsoluteImageUrl(item.cover_image)
            }));
            
            // 合并到现有结果
            setGameList(prev => [...prev, ...newResults]);
            setCurrentPage(prev => prev + 1);
            
            // 检查是否还有更多数据
            const total = result.data?.total || 0;
            setTotalResults(total);
            setHasMore(gameList.length + newResults.length < total);
            
        } catch (error) {
            if (currentRequestId === requestIdRef.current) {
                if (error.name === 'AbortError') {
                    console.log('Load more request was aborted');
                } else {
                    console.error('Error loading more games:', error);
                }
            }
        } finally {
            if (currentRequestId === requestIdRef.current) {
                setLoadingMore(false);
            }
        }
    }, [searchInput, currentPage, loadingMore, hasMore, gameList.length]);

    const fetchGames = async (query = searchInput, isNewSearch = false) => {
        // 使用gRPC搜索API
        if (!query.trim()) {
            setGameList([]);
            setCurrentPage(1);
            setHasMore(false);
            setTotalResults(0);
            return;
        }

        // 如果是新的搜索查询，重置状态
        if (isNewSearch || query !== lastSearchQueryRef.current) {
            setCurrentPage(1);
            setGameList([]);
            setHasMore(false);
            setTotalResults(0);
            lastSearchQueryRef.current = query;
        }

        // 生成当前请求的序列号
        const currentRequestId = ++requestIdRef.current;

        try {
            // 取消之前的请求
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
            
            // 创建新的AbortController
            abortControllerRef.current = new AbortController();

            setFetchLoading(true);
            
            // 使用封装的grpcApi搜索方法
            const result = await grpcApi.game.searchGames({
                query: query.trim(),
                page: 1,
                page_size: 10
            }, abortControllerRef.current);

            // 检查请求是否已经过期（避免竞争条件）
            if (currentRequestId !== requestIdRef.current) {
                console.log('Search request is outdated, ignoring result');
                return;
            }

            // 安全处理搜索结果
            const rawResults = result.data?.results;
            if (!Array.isArray(rawResults)) {
                setGameList([]);
                setCurrentPage(1);
                setHasMore(false);
                setTotalResults(0);
                return;
            }

            const results = rawResults.map(item => ({
                ...item,
                path: item.slug,
                cover_image: ensureAbsoluteImageUrl(item.cover_image)
            }));
            
            setGameList(results);
            setCurrentPage(1);
            
            // 检查是否还有更多数据
            const total = result.data?.total || 0;
            setTotalResults(total);
            setHasMore(results.length < total);
        } catch (error) {
            // 只有当前请求才处理错误
            if (currentRequestId === requestIdRef.current) {
                if (error.name === 'AbortError') {
                    console.log('Search request was aborted');
                } else {
                    console.error('Error searching games:', error);
                    setGameList([]);
                    setCurrentPage(1);
                    setHasMore(false);
                    setTotalResults(0);
                }
            }
        } finally {
            // 只有当前请求才设置loading状态
            if (currentRequestId === requestIdRef.current) {
                setFetchLoading(false);
            }
        }
    };

    const onSearchChange = (value) => {
        if (!value) {
            setSearchInput("");
            setGameList([]); // 清空搜索框时清空游戏列表
            setCurrentPage(1);
            setHasMore(false);
            setTotalResults(0);
            lastSearchQueryRef.current = "";
            return;
        }
        setSearchInput(value);
        searchGame(value, true); // 标记为新搜索
    };

    const searchGame = debounce(async (query, isNewSearch = false) => {
        await fetchGames(query, isNewSearch);
    }, 800);

    const selectGame = (gameSlug, option) => {
        const selectedGame = gameList.find(game => game.slug === gameSlug);
        if (selectedGame) {
            selectedGame.clicked_count++;
            gameClicked(selectedGame);
            dispatch(setCurrentGame(selectedGame));
            router.push(`/game/${gameSlug}`);
            combobox.closeDropdown();
        }
    };

    // 处理下拉框滚动事件
    const handleScroll = useCallback((e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        const isNearBottom = scrollTop + clientHeight >= scrollHeight - 10;
        
        if (isNearBottom && hasMore && !loadingMore && !fetchLoading) {
            fetchMoreGames();
        }
    }, [hasMore, loadingMore, fetchLoading, fetchMoreGames]);

    const options = filterGameList.map((item) => (
        <Combobox.Option value={item.value} key={item.value} className={styles.option_item}>
            <div className={styles.option_content}>
                <div className={styles.option_image}>
                    <Image
                        src={item.game.cover_image || '/images/icons/common/placeholder.jpg'}
                        alt={item.game.name}
                        width={40}
                        height={40}
                        className={styles.game_cover}
                    />
                </div>
                <div className={styles.option_text}>
                    <span className={styles.game_name}>{item.label}</span>
                </div>
            </div>
        </Combobox.Option>
    ));

    return (
        <Combobox
            store={combobox}
            onOptionSubmit={selectGame}
        >
            <Combobox.Target>
                <PillsInput
                    className={styles.search}
                    rightSection={searchIcon}
                    onClick={() => combobox.openDropdown()}
                    radius={24}
                    size="sm"
                >
                    <Pill.Group>
                        <PillsInput.Field
                            value={searchInput}
                            onChange={(e) => onSearchChange(e.target.value)}
                            placeholder="Search"
                            onFocus={() => combobox.openDropdown()}
                            onBlur={() => combobox.closeDropdown()}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    dispatch(setSearchQuery(searchInput));
                                }
                            }}
                        />
                    </Pill.Group>
                </PillsInput>
            </Combobox.Target>

            <Combobox.Dropdown className={styles.dropdown}>
                <Combobox.Options 
                    className={styles.combobox_options}
                    ref={dropdownRef}
                    onScroll={handleScroll}
                >
                    {fetchLoading ? (
                        <LoadingOverlay
                            visible={true}
                            zIndex={2000}
                            overlayProps={{ radius: 'sm', opacity: 0.1 }}
                            loaderProps={{ color: 'lightgrey', type: 'bars' }}
                        />
                    ) : options.length > 0 ? (
                        <>
                            {options}
                            {/* 加载更多指示器 */}
                            {hasMore && (
                                <div className={styles.load_more_container}>
                                    {loadingMore ? (
                                        <div className={styles.loading_more}>
                                            <Loader size="sm" color="orange" />
                                            <Text size="sm" c="dimmed" ml="xs">loading...</Text>
                                        </div>
                                    ) : (
                                        <Text onClick={fetchMoreGames} style={{ cursor: 'pointer', background: 'rgb(255 234 207)', borderRadius: '16px' }} size="xs" c="dimmed" ta="center" py="xs">
                                            load more ({gameList.length}/{totalResults})
                                        </Text>
                                    )}
                                </div>
                            )}
                            {/* 没有更多数据提示 */}
                            {!hasMore && gameList.length > 0 && (
                                <div className={styles.no_more_container}>
                                    <Text size="xs" c="dimmed" ta="center" py="xs">
                                        No More Data
                                    </Text>
                                </div>
                            )}
                        </>
                    ) : (
                        <Combobox.Empty>Nothing found...</Combobox.Empty>
                    )}
                </Combobox.Options>
            </Combobox.Dropdown>
        </Combobox>
    );
}